import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Bars3Icon, ChevronDownIcon, PlusIcon } from '@heroicons/react/20/solid'
import { TrashIcon } from '@heroicons/react/24/outline'

import BrandColorButton from 'components/Buttons/BrandColorButton'
import MicroVolumeStimulusTypeIcon from 'components/Program/MicroVolumeConfig/MicroVolumeStimulusTypeIcon'

const rowTransition = { duration: 0.2, ease: 'easeOut' }

export default function ProgramMetadataEditor({
    title,
    items = [],
    OnSave = async () => {},
    addLabel = 'Add item',
    emptyLabel = 'No items yet',
    newItemName = 'New Item',
    defaultColorHex = '#e5e7eb',
    showColorHex = true,
}) {

    const [expanded, setExpanded] = useState(false)
    const [draft, setDraft] = useState(items)
    const [draggedIndex, setDraggedIndex] = useState(null)
    const [dragEnabled, setDragEnabled] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const HandleDragEnter = (index) => {
        if (draggedIndex === null || draggedIndex === index) {
            return
        }

        setDraft((prev) => {
            const updated = [...prev]
            const [moved] = updated.splice(draggedIndex, 1)
            updated.splice(index, 0, moved)
            return updated.map((item, sortIndex) => ({ ...item, sortOrder: sortIndex + 1 }))
        })

        setDraggedIndex(index)
    }

    const HandleDragEnd = () => {
        setDraggedIndex(null)
        setDragEnabled(false)
    }

    const AddItem = () => {
        setDraft((prev) => {
            const nextSortOrder = prev.reduce((max, item) => Math.max(max, item.sortOrder ?? 0), 0) + 1

            const newItem = {
                name: newItemName,
                sortOrder: nextSortOrder,
                _localKey: crypto.randomUUID(),
            }

            if (showColorHex) {
                newItem.colorHex = defaultColorHex
            }

            return [...prev, newItem]
        })
    }

    const RemoveItem = (index) => {
        setDraft((prev) => (
            prev
                .filter((_, itemIndex) => itemIndex !== index)
                .map((item, sortIndex) => ({ ...item, sortOrder: sortIndex + 1 }))
        ))
    }

    const UpdateItem = (index, key, value) => {
        setDraft((prev) => (
            prev.map((item, itemIndex) => (
                itemIndex === index
                    ? { ...item, [key]: value }
                    : item
            ))
        ))
    }

    const SaveChanges = async () => {
        setIsSaving(true)

        const payload = draft.map(({ _localKey, ...item }) => item)
        
        await OnSave(payload)

        setIsSaving(false)
    }

    useEffect(() => { setDraft(items) }, [items])

    const hasChanges = JSON.stringify(draft.map(({ _localKey, ...item }) => item)) !== JSON.stringify(items)

    return (
        <div className='w-full'>
            <div
                className='flex items-center gap-x-4 px-4 py-3.5 cursor-pointer'
                onClick={() => setExpanded(!expanded)}
            >
                <p className='flex-1 text-lg select-none'>
                    {title}
                </p>

                <ChevronDownIcon
                    className={`w-6 h-6 text-gray-600 transition-transform duration-200 ease-out ${expanded ? 'rotate-180' : 'rotate-0'}`}
                />
            </div>

            <div
                className={`
                    grid transition-[grid-template-rows] duration-200 ease-out
                    ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
                `}
            >
                <div className='overflow-hidden'>
                    <div
                        className={`
                            w-full border-t border-gray-200 bg-gray-50 transition-opacity duration-200 ease-out
                            ${expanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                        `}
                    >
                        <div className='divide-y divide-gray-100'>
                            <AnimatePresence initial={false}>
                                {draft.length === 0 &&
                                    <motion.p
                                        key='empty'
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={rowTransition}
                                        className='overflow-hidden px-4 py-3 text-sm text-gray-400'
                                    >
                                        {emptyLabel}
                                    </motion.p>
                                }

                                {draft.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        layout={!dragEnabled}
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={rowTransition}
                                        className='overflow-hidden'
                                    >
                                        <div
                                            draggable={dragEnabled}
                                            onDragStart={() => setDraggedIndex(index)}
                                            onDragEnter={() => HandleDragEnter(index)}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDragEnd={HandleDragEnd}
                                            className={`
                                                flex items-center gap-x-3 px-4 py-2.5
                                                ${draggedIndex === index ? 'opacity-40 bg-gray-100' : ''}
                                                ${dragEnabled ? 'cursor-grabbing' : ''}
                                            `}
                                        >
                                            {/* 
                                            <button
                                                type='button'
                                                title='Drag to rearrange'
                                                className='h-7 w-7 flex-shrink-0 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing'
                                                onMouseDown={() => setDragEnabled(true)}
                                                onMouseUp={() => setDragEnabled(false)}
                                            >
                                                <Bars3Icon className='h-5 w-5' />
                                            </button> 
                                            */}

                                            {showColorHex &&
                                                <label className='relative h-4 w-4 flex-shrink-0 cursor-pointer'>
                                                    <span
                                                        className='absolute inset-0 rounded-full border border-gray-300'
                                                        style={{ backgroundColor: item.colorHex }}
                                                    />
                                                    <input
                                                        type='color'
                                                        value={/^#[0-9a-fA-F]{6}$/.test(item.colorHex) ? item.colorHex : defaultColorHex}
                                                        onChange={(e) => UpdateItem(index, 'colorHex', e.target.value)}
                                                        className='absolute inset-0 opacity-0 cursor-pointer'
                                                    />
                                                </label>
                                            }

                                            {item.iconKey &&
                                                <span className='flex h-5 w-5 flex-shrink-0 items-center justify-center'>
                                                    <MicroVolumeStimulusTypeIcon iconKey={item.iconKey} />
                                                </span>
                                            }

                                            <input
                                                type='text'
                                                value={item.name}
                                                // onChange={(e) => UpdateItem(index, 'name', e.target.value)}
                                                className='min-w-0 flex-1 border-0 bg-transparent py-1 text-sm text-gray-800 focus:outline-none focus:ring-0'
                                                disabled
                                            />

                                            {showColorHex &&
                                                <input
                                                    type='text'
                                                    value={item.colorHex}
                                                    onChange={(e) => UpdateItem(index, 'colorHex', e.target.value)}
                                                    className='w-20 flex-shrink-0 border-0 bg-transparent py-1 text-right text-sm text-gray-400 focus:outline-none focus:ring-0'
                                                />
                                            }

                                            {/* 
                                            <button
                                                type='button'
                                                title='Remove item'
                                                onClick={() => RemoveItem(index)}
                                                className='h-7 w-7 flex-shrink-0 flex items-center justify-center rounded-md bg-gray-50 text-gray-400 hover:bg-gray-100'
                                            >
                                                <TrashIcon className='h-4 w-4' />
                                            </button> 
                                            */}
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* 
                        <div className='flex items-center justify-between gap-x-3 border-t border-gray-200 bg-gray-50 px-4 py-2.5'>
                            <button
                                type='button'
                                onClick={AddItem}
                                className='flex items-center gap-x-2 text-sm text-gray-500 hover:text-gray-700'
                            >
                                <PlusIcon className='h-4 w-4' />
                                {addLabel}
                            </button>

                            <div
                                className={`
                                    w-24 transition-opacity duration-200 ease-out
                                    ${hasChanges ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                                `}
                            >
                                <BrandColorButton
                                    size='extra-small'
                                    text='Save'
                                    action={SaveChanges}
                                    isLoading={isSaving}
                                    className='shadow-sm'
                                />
                            </div>
                        </div> 
                        */}
                    </div>
                </div>
            </div>
        </div>
    )
}
